import { useEffect, useState } from "react";
import { SessionStatus, useConnectionView, useTrading } from "@helm/core";
import { networkStatus } from "@helm/ui";
import { ChartLatency } from "../chart/ChartLatency";
import { onChartStats } from "../chart/renderHost";
import { useMdStatus } from "../md/marketData";
import { PlantDots } from "./PlantDots";
import "./diagnostics.css";

declare const __HELM_VERSION__: string;

/**
 * Connection diagnostics (owner 2026-10-04 UI clean-up): what the old bottom status bar showed —
 * the Rithmic plants (T O H P), the gateway stream and its round trips, network state, the chart
 * history cache, receive-to-render latency, feeds up and the app version — in one small panel. It
 * opens from the status line's connection menu and shows in Settings › Connection › Diagnostics.
 * It reads the stores itself, so it takes no props, and it only exists while it is open.
 */
export function DiagnosticsPanel(): React.JSX.Element {
  const connection = useConnectionView();
  const connected = connection.status === "connected";
  const md = useMdStatus();
  const sessionReady = useTrading((s) => s.session?.status === SessionStatus.READY);
  const [cache, setCache] = useState<string>("—");
  const [chartMd, setChartMd] = useState<string | null>(null);
  useEffect(
    () =>
      onChartStats((s) => {
        setCache(s.history.cache === "idb" ? "warm" : s.history.cache === "none" ? "off" : "…");
        setChartMd(s.md);
      }),
    [],
  );
  const mdUp = md === "connected" || chartMd === "connected";
  const feeds: [string, boolean][] = [
    ["Gateway (trading stream)", connected],
    ["Market data stream", mdUp],
    ["Rithmic order plant", sessionReady],
  ];
  const up = feeds.filter(([, ok]) => ok).length;
  const net = networkStatus(connection);
  const version = typeof __HELM_VERSION__ === "string" ? __HELM_VERSION__ : "dev";
  const ms = (v: number | null): string => (v === null || v === 0 ? "—" : `${String(v)} ms`);
  return (
    <section className="diagnostics" aria-label="Diagnostics">
      <dl className="diagnostics-list">
        <dt>Rithmic plants</dt>
        <dd>
          <PlantDots connected={connected} />
        </dd>
        <dt>Gateway stream</dt>
        <dd>
          <span className={connected ? "dot up" : "dot down"}>GW</span> {connected ? "up" : "down"}
          {connection.serverMode ? <span className="muted"> · {connection.serverMode}</span> : null}
        </dd>
        <dt>Round trip</dt>
        <dd className="num" title="Browser ↔ gateway, and gateway ↔ Rithmic">
          GW {ms(connection.gwRttMs)} · RTH {ms(connection.rithmicRttMs)}
        </dd>
        <dt>Network</dt>
        <dd>{net.label}</dd>
        <dt>Render latency</dt>
        <dd>
          <ChartLatency />
        </dd>
        <dt>Feeds</dt>
        <dd className={up === feeds.length ? undefined : "status-warn"}>
          {up}/{feeds.length}
          <ul className="diagnostics-feeds">
            {feeds.map(([n, ok]) => (
              <li key={n}>
                {ok ? "●" : "○"} {n}: {ok ? "up" : "down"}
              </li>
            ))}
          </ul>
        </dd>
        <dt>Chart cache</dt>
        <dd title="Chart history cache (SPEC-ADDENDA A1): warm = IndexedDB cache in use, off = fetched every time">
          {cache}
        </dd>
        <dt>Version</dt>
        <dd className="num">v{version}</dd>
      </dl>
    </section>
  );
}
